/* ═══════════════════════════════════════
   AirLog Media Helpers
   Upload, save, and share media files
   ═══════════════════════════════════════ */

   import { supabase } from '../supabaseClient'

   /* ── Upload tuning ──────────────────────────────────────────────
    * UPLOAD_TIMEOUT_MS : abort a single file's upload if it neither
    *   succeeds nor errors within this window (weak wifi can leave a
    *   request hanging forever — this is what caused "stuck sending").
    * MAX_RETRIES       : how many times to retry a failed/timed-out file.
    * RETRY_BASE_MS     : backoff base; wait grows each attempt.
    * MAX_CONCURRENT    : how many files upload AT ONCE. Parallel (not
    *   sequential) so one slow file doesn't block the rest, but capped
    *   so we don't saturate a weak connection. */
   const UPLOAD_CONFIG = {
     UPLOAD_TIMEOUT_MS: 45000,
     MAX_RETRIES: 2,
     RETRY_BASE_MS: 1200,
     MAX_CONCURRENT: 3,
   }

   const wait = (ms) => new Promise(r => setTimeout(r, ms))

   /* Upload a single file with a timeout + retries. Resolves to a result
    * object on success, or null after exhausting retries (caller decides
    * what to do with a null). Never throws, never hangs forever. */
   const uploadOneFile = async (file, userId) => {
     const ext = file.name.split('.').pop()
     for (let attempt = 0; attempt <= UPLOAD_CONFIG.MAX_RETRIES; attempt++) {
       // Fresh path each attempt so a half-written object never collides.
       const path = `${userId}/${Date.now()}-${Math.random().toFixed(6)}.${ext}`
       try {
         // Race the upload against a timeout. Supabase's JS client doesn't
         // accept an AbortSignal here, so we race promises: whichever
         // settles first wins. A timeout throws and triggers a retry.
         const uploadPromise = supabase.storage.from('media').upload(path, file)
         const timeoutPromise = wait(UPLOAD_CONFIG.UPLOAD_TIMEOUT_MS).then(() => {
           throw new Error('upload-timeout')
         })
         const { error } = await Promise.race([uploadPromise, timeoutPromise])
         if (error) throw error

         const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(path)
         return { url: publicUrl, type: file.type, name: file.name }
       } catch (err) {
         console.error(`upload attempt ${attempt + 1} failed for ${file.name}:`, err?.message || err)
         if (attempt < UPLOAD_CONFIG.MAX_RETRIES) {
           await wait(UPLOAD_CONFIG.RETRY_BASE_MS * (attempt + 1))   // backoff
           continue
         }
         return null   // exhausted — caller handles the failure
       }
     }
     return null
   }

   /* Upload many files. Runs up to MAX_CONCURRENT at once (so one slow
    * file never blocks the others), each with its own timeout + retry.
    * Returns the successful results IN THE ORIGINAL ORDER. If some files
    * fail entirely, they're omitted but the rest still go through —
    * partial success beats an all-or-nothing hang. */
   export const uploadFiles = async (files, userId) => {
     const fileArr = Array.from(files)
     const results = new Array(fileArr.length).fill(null)
     let cursor = 0

     // A worker pulls the next index off the shared cursor until done.
     const worker = async () => {
       while (cursor < fileArr.length) {
         const i = cursor++
         results[i] = await uploadOneFile(fileArr[i], userId)
       }
     }

     const workerCount = Math.min(UPLOAD_CONFIG.MAX_CONCURRENT, fileArr.length)
     await Promise.all(Array.from({ length: workerCount }, () => worker()))

     // Drop failures, keep order. (Caller already handles an empty result.)
     return results.filter(Boolean)
   }

   /* iOS detection — used to hide save button (iOS share sheet has Save Photo built in) */
   export const isIOS = () => {
     if (typeof navigator === 'undefined') return false
     const ua = navigator.userAgent
     return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document)
   }

   /* Original save — auto-download to Downloads folder (used as fallback) */
   export const saveMediaToDevice = async (url) => {
     try {
       const response = await fetch(url, { mode: 'cors' })
       if (!response.ok) throw new Error('fetch failed')
       const blob = await response.blob()
       const blobUrl = URL.createObjectURL(blob)
       const a = document.createElement('a')
       a.href = blobUrl
       const ext = url.split('.').pop()?.split('?')[0] || 'jpg'
       a.download = `airlog-${Date.now()}.${ext}`
       document.body.appendChild(a)
       a.click()
       document.body.removeChild(a)
       setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
     } catch {
       const a = document.createElement('a')
       a.href = url
       a.download = `airlog-${Date.now()}`
       a.target = '_blank'
       a.rel = 'noopener noreferrer'
       document.body.appendChild(a)
       a.click()
       document.body.removeChild(a)
     }
   }

   /* Save with native file picker — pops "Save As..." dialog on supported browsers
    * (Chrome/Edge desktop), falls back to auto-download on unsupported (Firefox,
    * Safari, mobile browsers). User cancellation is silent.
    */
   export const saveMediaWithPicker = async (url, suggestedName) => {
     const fallbackName = suggestedName || `airlog-${Date.now()}`

     // Path 1: File System Access API (Chrome/Edge desktop) — real save dialog
     if (typeof window !== 'undefined' && 'showSaveFilePicker' in window) {
       try {
         const response = await fetch(url, { mode: 'cors' })
         if (!response.ok) throw new Error('fetch failed')
         const blob = await response.blob()
         const ext = fallbackName.split('.').pop()?.toLowerCase() || ''
         const mime = blob.type || 'application/octet-stream'
         const handle = await window.showSaveFilePicker({
           suggestedName: fallbackName,
           types: ext ? [{ description: 'File', accept: { [mime]: [`.${ext}`] } }] : undefined,
         })
         const writable = await handle.createWritable()
         await writable.write(blob)
         await writable.close()
         return
       } catch (err) {
         // AbortError = user cancelled the picker, exit silently
         if (err.name === 'AbortError') return
         // Other errors fall through to download fallback
       }
     }

     // Path 2: Fallback — auto-download (existing behavior)
     await saveMediaToDevice(url)
   }

   /* ── Cache: pre-fetched blobs keyed by URL ──
    * iOS Safari requires navigator.share() to fire SYNCHRONOUSLY from the user
    * gesture. Awaiting fetch() before share() breaks the gesture and iOS silently
    * rejects. So we prefetch the blob via prefetchForShare() when the viewer
    * opens, then shareMedia() pulls from cache and calls navigator.share()
    * instantly with no awaits. */
   const shareCache = new Map()

   export const prefetchForShare = async (url, filename) => {
     if (!url || shareCache.has(url)) return
     try {
       const response = await fetch(url, { mode: 'cors' })
       if (!response.ok) return
       const blob = await response.blob()
       const ext = filename?.split('.').pop() || url.split('.').pop()?.split('?')[0] || 'jpg'
       const name = filename || `airlog-${Date.now()}.${ext}`
       const file = new File([blob], name, { type: blob.type || 'application/octet-stream' })
       shareCache.set(url, file)
     } catch {
       /* swallow — shareMedia will fall back to URL share */
     }
   }

   export const clearShareCache = () => {
     shareCache.clear()
   }

   export const shareMedia = async (url, filename) => {
     const cachedFile = shareCache.get(url)

     // Path 1 — file in cache: share synchronously, preserves iOS user gesture
     if (cachedFile && navigator.canShare && navigator.canShare({ files: [cachedFile] })) {
       try {
         await navigator.share({ files: [cachedFile], title: cachedFile.name })
         return
       } catch (err) {
         if (err.name === 'AbortError') return // user cancelled
       }
     }

     // Path 2 — no cached file: fetch and share (works on desktop / Android, may fail iOS gesture)
     if (!cachedFile) {
       try {
         const response = await fetch(url, { mode: 'cors' })
         if (response.ok) {
           const blob = await response.blob()
           const ext = filename?.split('.').pop() || url.split('.').pop()?.split('?')[0] || 'jpg'
           const name = filename || `airlog-${Date.now()}.${ext}`
           const file = new File([blob], name, { type: blob.type || 'application/octet-stream' })
           if (navigator.canShare && navigator.canShare({ files: [file] })) {
             try {
               await navigator.share({ files: [file], title: name })
               return
             } catch (err) {
               if (err.name === 'AbortError') return
             }
           }
         }
       } catch { /* fall through */ }
     }

     // Path 3 — URL share (no file support)
     try {
       if (navigator.share) {
         await navigator.share({ title: filename || 'AirLog Media', url })
         return
       }
     } catch (err) {
       if (err.name === 'AbortError') return
     }

     // Path 4 — last resort: open in new tab
     window.open(url, '_blank')
   }